import { getStorageSafely } from '../state/browserStorage';
import type { StateCreator } from 'zustand';
import type { Action, ReduxDevtoolsExtension } from './reduxDevtools.types';

type ReduxDevtoolsStoreState = {
  state: unknown;
};

type ReduxDevtoolsStoreStateGetter = () => ReduxDevtoolsStoreState | { state: unknown } | unknown;

export type ReduxDevtoolsActionContext = {
  currentActionType?: string;
};

export function toReduxDevtoolsStoreName(storeName: string) {
  return `sq${storeName}Store`;
}

export const createReduxDevtoolsMiddleware =
  <S extends object>(storeName: string, actionContext: ReduxDevtoolsActionContext) =>
  (config: StateCreator<S, [], []>): StateCreator<S, [], []> =>
  (set, get, api) => {
    const devtoolsStoreName = toReduxDevtoolsStoreName(storeName);

    const syncStoreToReduxDevtools = (actionType = 'setState') => {
      if (reduxDevtoolsConnection && isDebugStoresDevtoolsEnabled()) {
        sendDevtoolsState({ type: `${devtoolsStoreName}/${actionType}` });
      }
    };

    const trackedSet: typeof set = ((state, replace) => {
      set(state as Parameters<typeof set>[0], replace as Parameters<typeof set>[1]);
      syncStoreToReduxDevtools(actionContext.currentActionType);
    }) as typeof set;

    api.setState = ((state, replace) => {
      set(state as Parameters<typeof set>[0], replace as Parameters<typeof set>[1]);
      syncStoreToReduxDevtools(actionContext.currentActionType);
    }) as typeof api.setState;

    return config(trackedSet, get, api);
  };

export function wrapActionsWithReduxDevtools<A extends object>(
  actions: A,
  _storeName: string,
  actionContext: ReduxDevtoolsActionContext,
): A {
  for (const [actionName, action] of Object.entries(actions as Record<string, unknown>)) {
    if (typeof action !== 'function') {
      continue;
    }

    (actions as Record<string, unknown>)[actionName] = (...args: unknown[]) => {
      actionContext.currentActionType = actionName;
      try {
        return action(...args);
      } finally {
        actionContext.currentActionType = undefined;
      }
    };
  }

  return actions;
}

let reduxDevtoolsConnection: ReturnType<ReduxDevtoolsExtension['connect']> | undefined;

let reduxDevtoolsStores: Record<string, ReduxDevtoolsStoreStateGetter> = {};
let isDebugStoresEnabled: boolean | undefined;

export function setReduxDevtoolsConnectionForTests(
  connection: ReturnType<ReduxDevtoolsExtension['connect']> | undefined,
) {
  reduxDevtoolsConnection = connection;
}

export const registerReduxDevtoolsStore = (storeName: string, getStoreState: ReduxDevtoolsStoreStateGetter) => {
  reduxDevtoolsStores[storeName] = getStoreState;
};

export const getAllDevtoolsStoreStates = () =>
  Object.fromEntries(
    Object.entries(reduxDevtoolsStores).map(([storeName, getStoreState]) => [storeName, getStoreState()]),
  );

export const sendDevtoolsState = (action: Action) => {
  reduxDevtoolsConnection?.send(action, getAllDevtoolsStoreStates());
};

export function isDebugStoresDevtoolsEnabled() {
  if (isDebugStoresEnabled !== undefined) {
    return isDebugStoresEnabled;
  }

  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
    isDebugStoresEnabled = false;
    return isDebugStoresEnabled;
  }

  const debugStoresSearchParam =
    new URLSearchParams(window.location.search).get('debug-stores')?.trim().toLowerCase() ?? '';
  const storage = getStorageSafely();

  if (debugStoresSearchParam && debugStoresSearchParam !== 'true') {
    isDebugStoresEnabled = false;
    return isDebugStoresEnabled;
  }

  const debugStoresLocalStorage = storage.getItem('debug-stores') !== null;

  isDebugStoresEnabled = debugStoresSearchParam === 'true' || debugStoresLocalStorage;
  return isDebugStoresEnabled;
}

export const initializeReduxDevtools = () => {
  function stateSanitizer(stores: Record<string, { state: unknown }>) {
    return Object.fromEntries(Object.entries(stores).map(([storeName, store]) => [storeName, store.state]));
  }

  if (!isDebugStoresDevtoolsEnabled()) {
    getStorageSafely().removeItem('debug-stores');
    return;
  }

  if (reduxDevtoolsConnection) {
    return;
  }

  reduxDevtoolsConnection = (
    window as Window & { __REDUX_DEVTOOLS_EXTENSION__?: ReduxDevtoolsExtension }
  ).__REDUX_DEVTOOLS_EXTENSION__?.connect({
    name: 'All stores',
    stateSanitizer,
  });
  reduxDevtoolsConnection?.init(getAllDevtoolsStoreStates());

  reduxDevtoolsConnection?.subscribe((data) => {
    if (data?.type !== 'DISPATCH' || data.payload?.type === 'PAUSE_RECORDING') {
      return;
    }

    switch (data.payload?.type) {
      case 'COMMIT':
        reduxDevtoolsConnection?.init(getAllDevtoolsStoreStates());
        break;
      case 'RESET':
        reduxDevtoolsConnection?.init({});
        console.error('Redux DevTools action not fully supported: RESET');
        break;
      default:
        console.error(`Redux DevTools action not fully supported: ${data.payload?.type ?? 'unknown'}`);
        break;
    }
  });

  getStorageSafely().setItem('debug-stores', 'true');
};

export function resetReduxDevtoolsForTests() {
  reduxDevtoolsConnection = undefined;
  reduxDevtoolsStores = {};
  isDebugStoresEnabled = undefined;
}
